import React, { useState, useEffect } from 'react';
import { Container, Box, Title, Text, Button, Avatar, Paper, Group, Loader, Badge, Anchor, Modal, Textarea, RingProgress, Stack, ActionIcon } from '@mantine/core';
import { IconBriefcase, IconRobot, IconMapPin, IconWorld, IconPhone, IconLanguage } from '@tabler/icons-react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const AITranslator = ({ text, email }) => {
  const [translatedText, setTranslatedText] = useState('');
  const [loadingLang, setLoadingLang] = useState(null);

  if (!text) return null;

  const handleTranslate = async (lang) => {
    setLoadingLang(lang);
    try {
      const res = await fetch(`${API_BASE_URL}/api/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerEmail: email, text, targetLanguage: lang })
      });
      const data = await res.json();
      if (data.translatedText) {
        setTranslatedText(data.translatedText);
      }
    } catch (e) {
      console.error(e);
    }
    setLoadingLang(null);
  };

  return (
    <Box>
      <Group gap="xs" mb="xs">
        <IconLanguage size={16} color="gray" />
        <ActionIcon size="sm" variant={translatedText && loadingLang !== 'English' ? 'light' : 'subtle'} color="indigo" onClick={() => handleTranslate('English')} loading={loadingLang === 'English'} title="Translate to English"><Text size="xs" fw={700}>EN</Text></ActionIcon>
        <ActionIcon size="sm" variant={translatedText && loadingLang !== 'Polish' ? 'light' : 'subtle'} color="indigo" onClick={() => handleTranslate('Polish')} loading={loadingLang === 'Polish'} title="Translate to Polish"><Text size="xs" fw={700}>PL</Text></ActionIcon>
        <ActionIcon size="sm" variant={translatedText && loadingLang !== 'German' ? 'light' : 'subtle'} color="indigo" onClick={() => handleTranslate('German')} loading={loadingLang === 'German'} title="Translate to German"><Text size="xs" fw={700}>DE</Text></ActionIcon>
      </Group>
      <Text size="sm" style={{ lineHeight: 1.6, color: '#475569', whiteSpace: 'pre-wrap' }}>
        {translatedText || String(text)}
      </Text>
    </Box>
  );
};

export default function JobProfile() {
  const [jobId, setJobId] = useState(null);
  const [jobData, setJobData] = useState(null);
  const [companyConfig, setCompanyConfig] = useState({});
  const [loading, setLoading] = useState(true);

  // AI Verification State
  const [modalOpened, setModalOpened] = useState(false);
  const [cvText, setCvText] = useState('');
  const [evaluation, setEvaluation] = useState(null);
  const [evaluating, setEvaluating] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('job');
    if (id) {
      setJobId(id);
    }
  }, []);

  useEffect(() => {
    if (!jobId) return;

    fetch(`${API_BASE_URL}/api/announcements/public`)
      .then(r => r.json())
      .then(data => {
        const found = data.find(a => String(a.id) === String(jobId));
        if (found) {
          setJobData(found);
          // Fetch company details
          fetch(`${API_BASE_URL}/api/config?ownerEmail=${found.ownerEmail}`)
            .then(r => r.json())
            .then(conf => {
              setCompanyConfig(conf || {});
              setLoading(false);
            })
            .catch(() => setLoading(false));
        } else {
          setLoading(false);
        }
      })
      .catch(e => {
        console.error(e);
        setLoading(false);
      });
  }, [jobId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (cvText.trim().length > 20 && jobData) {
        handleEvaluate();
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [cvText, jobData]);

  const handleEvaluate = async () => {
    if (!cvText.trim() || !jobData) return;
    setEvaluating(true);
    setEvaluation(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/announcements/${jobData.id}/evaluate-cv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cvText })
      });
      if (res.ok) {
        setEvaluation(await res.json());
      } else {
        setEvaluation({ error: 'Evaluation failed. Please try again later.' });
      }
    } catch (e) {
      console.error(e);
      setEvaluation({ error: 'Network error. Please try again later.' });
    } finally {
      setEvaluating(false);
    }
  };

  if (!jobId) return null;

  if (loading) {
    return (
      <Box style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc' }}>
        <Loader size="lg" color="indigo" />
      </Box>
    );
  }

  if (!jobData) {
    return (
      <Box style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc' }}>
        <Title order={3} c="dimmed">Job offer not found or no longer available.</Title>
      </Box>
    );
  }

  const companyName = companyConfig['company.name'] || jobData.ownerEmail.split('@')[0].toUpperCase();
  const address = companyConfig['company.address'] || '';
  const website = companyConfig['company.website'] || '';

  return (
    <Box style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header / Hero */}
      <Box style={{ backgroundColor: companyConfig['company.bg.color'] || '#4f46e5', padding: '40px 20px', color: 'white' }}>
        <Container size="md">
          <Group align="flex-start" wrap="nowrap">
            {companyConfig['company.logo'] && (
              <Avatar size={80} radius="md" src={String(companyConfig['company.logo'])} />
            )}
            <Box style={{ flex: 1 }}>
              <Title order={2} style={{ fontWeight: 800 }}>{String(jobData.title)}</Title>
              <Text size="lg" opacity={0.9} mb="sm">{String(companyName)}</Text>
              <Group gap="xl" mt="sm">
                {address && (
                  <Group gap="xs" style={{ opacity: 0.9 }}><IconMapPin size={16}/><Text size="sm">{String(address)}</Text></Group>
                )}
                {website && (
                  <Group gap="xs" style={{ opacity: 0.9 }}><IconWorld size={16}/><Anchor href={String(website).startsWith('http') ? String(website) : 'https://'+String(website)} target="_blank" c="white" size="sm">{String(website)}</Anchor></Group>
                )}
                <Badge color="white" variant="light" size="lg">{String(jobData.type === 'JOB' ? 'Job Offer' : 'Offer')}</Badge>
              </Group>
            </Box>
          </Group>
        </Container>
      </Box>

      <Container size="md" py="xl">
        <Paper p="xl" radius="md" style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
          <Title order={4} mb="md" style={{ color: '#1e293b' }}>Job Description</Title>
          <Box style={{ backgroundColor: 'white', borderRadius: '8px' }}>
            <AITranslator text={jobData.description} email={jobData.ownerEmail} />
          </Box>
        </Paper>

        <Paper p="xl" radius="md" style={{ backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', textAlign: 'center' }}>
          <IconRobot size={40} color="#4f46e5" style={{ marginBottom: '16px' }}/>
          <Title order={4} mb="xs" style={{ color: '#312e81' }}>Apply with AI</Title>
          <Text size="sm" c="dimmed" mb="lg" style={{ maxWidth: '500px', margin: '0 auto 20px' }}>
            We use AI to screen candidates. Paste your CV/Resume text below and get instant feedback on whether you're a good fit for this role!
          </Text>
          <Button size="lg" color="indigo" onClick={() => setModalOpened(true)}>
            Verify My CV
          </Button>
        </Paper>
      </Container>

      {/* Verification Modal */}
      <Modal opened={modalOpened} onClose={() => setModalOpened(false)} title="Verify Your CV" size="lg">
        <Stack gap="md">
          <Text size="sm" c="dimmed">Paste your CV or Resume text below. Our AI will automatically evaluate your match for this job.</Text>
          <Title order={5}>{String(jobData.title)}</Title>
          
          <Textarea 
            label="Your CV/Resume Text"
            value={cvText}
            onChange={(e) => setCvText(e.target.value)}
            minRows={8}
            placeholder="Paste your experience, skills, and education here..."
            disabled={evaluating}
          />
          
          {evaluating && (
            <Group justify="center" mt="md">
              <Loader size="sm" color="indigo" />
              <Text size="sm" c="dimmed">AI is analyzing your profile...</Text>
            </Group>
          )}

          {evaluation && !evaluation.error && (
            <Paper withBorder mt="md" radius="md" p="md">
              <Group align="flex-start" wrap="nowrap">
                <RingProgress
                  size={80}
                  roundCaps
                  thickness={8}
                  sections={[{ value: evaluation.score || 0, color: (evaluation.score > 70 ? 'green' : evaluation.score > 40 ? 'yellow' : 'red') }]}
                  label={
                    <Text ta="center" size="sm" fw={700}>
                      {evaluation.score}%
                    </Text>
                  }
                />
                <Box>
                  <Title order={5} mb="xs">AI Feedback</Title>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{evaluation.feedback}</Text>
                </Box>
              </Group>
            </Paper>
          )}

          {evaluation?.error && (
            <Text c="red" size="sm" mt="md">{evaluation.error}</Text>
          )}
        </Stack>
      </Modal>

    </Box>
  );
}
